import { Component, inject, OnInit, OnDestroy, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { ProgressBarModule } from 'primeng/progressbar';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { Subscription } from 'rxjs';
import { SyncUserQuestionsService } from '../../../../api/api-services/sync-user-questions.service';
import { ISyncStatusVm } from '../../../../api/sync-status-vm.model';
import { SyncUsersQuestionsService } from '../../../../api/api-services/sync-users-questions.service';

@Component({
  selector: 'app-sync-progress-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, ProgressBarModule, ButtonModule, InputTextModule, TooltipModule],
  templateUrl: './sync-progress-dialog.component.html',
  styleUrl: './sync-progress-dialog.component.scss'
})
export class SyncProgressDialogComponent implements OnInit, OnDestroy {
  private readonly _dialogRef = inject(DynamicDialogRef);
  private readonly _syncUsersQuestionsService = inject(SyncUsersQuestionsService);

  protected readonly status = signal<ISyncStatusVm | null>(null);
  protected readonly loading = signal(false);
  protected readonly error = signal<string | null>(null);
  protected readonly resumeFromGuid = signal<string | null>(null);
  
  private pollSubscription?: Subscription;
  private isInitialLoad = true;

  protected readonly elapsedTime = computed(() => {
    const currentStatus = this.status();
    if (!currentStatus?.syncStartTime) return '0s';
    
    const startTime = new Date(currentStatus.syncStartTime).getTime();
    const endTime = currentStatus.isRunning 
      ? Date.now()
      : currentStatus.lastUpdateTime 
        ? new Date(currentStatus.lastUpdateTime).getTime() 
        : Date.now();
    
    const diffMs = endTime - startTime;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const sec = diffSec % 60;
    
    return diffMin > 0 ? `${diffMin}m ${sec}s` : `${diffSec}s`;
  });

  ngOnInit(): void {
    this.loadStatus();
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  startSync(): void {
    this.loading.set(true);
    this.error.set(null);

    const guid = this.resumeFromGuid()?.trim();
    const input = { resumeFromUserExamGuid: guid || null };
    
    this._syncUsersQuestionsService.postStart(input).subscribe({
      next: (started) => {
        this.loading.set(false);
        if (started) {
          this.resumeFromGuid.set('');
         
          this.startPolling();
          this.loadStatus();
        } else {
          this.error.set('Nie udało się rozpocząć synchronizacji');
        }
      },
      error: (err) => {
        this.error.set('Nie udało się rozpocząć synchronizacji');
        this.loading.set(false);
        console.error(err);
      }
    });
  }

  stopSync(): void {
    this.loading.set(true);
    this.error.set(null);

    this._syncUsersQuestionsService.postStop().subscribe({
      next: () => {
        this.stopPolling();
                const currentStatus = this.status();
        if (currentStatus) {
          this.status.set({ ...currentStatus, isRunning: false });
        }
        
        this.loading.set(false);
        
        setTimeout(() => {
          this.loadStatus();
        }, 500);
      },
      error: (err) => {
        this.error.set('Nie udało się zatrzymać synchronizacji');
        this.loading.set(false);
        console.error(err);
      }
    });
  }

  refreshStatus(): void {
    this.loadStatus();
  }

  useLastProcessedGuid(): void {
    const currentStatus = this.status();
    if (currentStatus?.lastProcessedUserExamGuid) {
      this.resumeFromGuid.set(currentStatus.lastProcessedUserExamGuid);
    }
  }

  clearGuid(): void {
    this.resumeFromGuid.set('');
  }

  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text).then(() => {
      console.log('GUID skopiowany do schowka');
    }).catch(err => {
      console.error('Błąd kopiowania do schowka:', err);
    });
  }

  closeDialog(): void {
    this._dialogRef.close(this.status());
  }

  private loadStatus(): void {
    this._syncUsersQuestionsService.get().subscribe({
      next: (status) => {
        this.status.set(status);
        
        if (this.isInitialLoad && status.isRunning) {
          this.startPolling();
        }
        this.isInitialLoad = false;
      },
      error: (err) => {
        this.error.set('Nie udało się pobrać statusu');
        console.error(err);
        this.isInitialLoad = false;
      }
    });
  }

  private startPolling(): void {
    this.stopPolling();
    
    this.pollSubscription = this._syncUsersQuestionsService.pollStatus(2000).subscribe({
      next: (status) => {
        this.status.set(status);
      },
      error: (err) => {
        console.error('Błąd podczas pobierania statusu:', err);
      }
    });
  }

  private stopPolling(): void {
    this.pollSubscription?.unsubscribe();
    this.pollSubscription = undefined;
  }
}
